/**
 * @fileoverview Request-error text for the Sales dialogs.
 * @description Turns a failed Sales request's error into the text a dialog shows. A server refusal
 * already carries a user-facing message, but an error the client raises itself (a prospect read
 * back without its version) must be translated here, so a Hindi or Marathi reader never sees an
 * English sentence from code.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Hardik Patel
 */

import type { TFunction } from 'i18next'

/** @description A prospect or approval was read back without the version a command must send. */
export class MissingVersionError extends Error {
  /**
   * @description Builds the error for a read that came back without its version (ETag).
   */
  constructor() {
    super('The prospect was returned without a version (ETag).')
    this.name = 'MissingVersionError'
  }
}

/**
 * @description The text to show for a failed Sales request.
 * @param {Error} error - The request's error.
 * @param {TFunction} t - The translation function.
 * @returns {string} The translated text for a client-raised error, else the error's own message.
 */
export function requestErrorMessage(error: Error, t: TFunction): string {
  return error instanceof MissingVersionError
    ? t('sales.pipeline.errors.missingVersion')
    : error.message
}
